import {Component, Input, OnInit, ViewChild} from '@angular/core';

import {switchMap, tap} from "rxjs/operators";
import {LocalStateService} from "../../../services/local-state.service";
import {Page, Pageable} from "../../../model/rest/base";
import {ModalComponent} from "../../modal/modal.component";
import {WorkflowsRestService} from "../../../services/rest/v3/workflows-rest.service";
import {
  ConfirmationRequestStatus,
  RequestType,
  V3ManualConfirmationRequest,
  V3School,
  V3SchoolConfirmationRequestExtended
} from "../../../model/v3/schools";
import {VerifiedPasswordChangeRequest} from "../../../model/v3/people";

@Component({
    selector: 'app-invitations-block',
    templateUrl: './invitations-block.component.html',
    styleUrls: ['./invitations-block.component.css'],
    standalone: false
})
export class InvitationsBlockComponent implements OnInit {
  private mSchool: V3School;
  private currentPageable: Pageable;
  private currentPage: Page<V3SchoolConfirmationRequestExtended>;
  private readonly localStateUrl = "dashboard/invitations";

  @ViewChild('activationModal', {static: true})
  activationModal: ModalComponent;
  confirmationToActivate: V3SchoolConfirmationRequestExtended;
  activationRequestForm: V3ManualConfirmationRequest;
  userPasswordRetype: string;
  private activationError: any = null;

  @Input()
  set school(value: V3School) {
    this.mSchool = value;
    this.reload();
  }

  constructor(
    private localState: LocalStateService,
    private workflowsRest: WorkflowsRestService
  ) { }

  ngOnInit() {}


  private reload() {
    if (!this.mSchool) return;
    this.currentPage = null;

    this.localState.get<Pageable>(
      this.localStateUrl,
      () => Pageable.of(0, 5, null))
      .pipe(
        switchMap( page => this.loadPage(page)),
        tap( dataPage => this.store(dataPage))
      )
      .subscribe();
  }

  private loadPage(page: Pageable) {
    this.currentPage = null;
    this.currentPageable = page;
    return this.workflowsRest.findConfirmationRequests(
      this.mSchool.id,
      Object.values(RequestType),
      [
        ConfirmationRequestStatus.Requested,
        ConfirmationRequestStatus.Expired,
      ],
      page
    );
  }

  private store(dataPage: Page<V3SchoolConfirmationRequestExtended>) {
    this.currentPage = dataPage;
  }

  isReady() {
    return this.currentPage;
  }

  hasPages() {
    return this.hasPrev() || this.hasNext();
  }

  hasPrev() {
    return this.currentPage && !this.currentPage.first;
  }

  prev() {
    this.localState.set(this.localStateUrl, this.currentPageable.prev())
      .pipe(
        switchMap( page => this.loadPage(page)),
        tap( pageData => this.store(pageData))
      )
      .subscribe();
  }

  hasNext() {
    return this.currentPage && !this.currentPage.last;
  }

  next() {
    this.localState.set(this.localStateUrl, this.currentPageable.next())
      .pipe(
        switchMap( page => this.loadPage(page)),
        tap( pageData => this.store(pageData))
      )
      .subscribe();
  }

  getEntries(): V3SchoolConfirmationRequestExtended[] {
    return this.currentPage.content;
  }

  getMail(entry: V3SchoolConfirmationRequestExtended) {
    if (!entry) return '';
    return entry.requestedEmail;
  }

  getRole(entry: V3SchoolConfirmationRequestExtended) {
    if (!entry) return '';
    switch (entry.role) {
      case RequestType.PersonDetailsUpdate:
        return "update";
      case RequestType.ManagerInvite:
        return "manager";
      case RequestType.TeacherInvite:
        return "teacher";
      case RequestType.StudentInvite:
        return "student";
      case RequestType.StudentInstantInvite:
        return "student";
    }
  }

  getExpiry(entry: V3SchoolConfirmationRequestExtended) {
    if (!entry) return null;
    return entry.expiryDate;
  }

  isEmpty() {
    return this.currentPage.totalElements === 0;
  }

  resend(entry: V3SchoolConfirmationRequestExtended) {
    this.workflowsRest.renewConfirmationRequest(this.mSchool.id, entry.role, entry.requestedEmail, entry.status).pipe(
      switchMap( _ => this.loadPage(this.currentPageable)),
      tap( data => this.store(data))
    ).subscribe();
  }

  revoke(entry: V3SchoolConfirmationRequestExtended) {
    this.workflowsRest.revokeConfirmationRequest(this.mSchool.id, entry.role, entry.requestedEmail, entry.status).pipe(
      switchMap( _ => this.loadPage(this.currentPageable)),
      tap( data => this.store(data))
    ).subscribe();
  }

  private rolesMayBeActivated = [
    RequestType.ManagerInvite, RequestType.TeacherInvite, RequestType.StudentInvite, RequestType.StudentInstantInvite];

  mayBeActivated(entry: V3SchoolConfirmationRequestExtended) {
    return (entry.status == ConfirmationRequestStatus.Requested
      && new Date(entry.expiryDate).getTime() > Date.now()
      && this.rolesMayBeActivated.indexOf(entry.role) >= 0
    )
  }

  activate(entry: V3SchoolConfirmationRequestExtended) {
    this.clearActivationData();
    this.confirmationToActivate = entry;
    this.activationModal.show();
  }

  private clearActivationData() {
    this.activationRequestForm = {} as V3ManualConfirmationRequest;
    this.activationRequestForm.passwords = {} as VerifiedPasswordChangeRequest;
    this.userPasswordRetype = null;
    this.activationError = null;
  }

  submitActivation() {
    if (!this.confirmationToActivate) {
      return;
    }
    this.workflowsRest.confirmRequestManually(
      this.mSchool.id,
      this.confirmationToActivate.role as any,
      this.confirmationToActivate.requestedEmail,
      this.confirmationToActivate.status as any,
      {
        passwords: {
          password: this.activationRequestForm.passwords.password,
          managerPassword: this.activationRequestForm.passwords.managerPassword,
        },
      }
      ).pipe(
        switchMap( _ => this.loadPage(this.currentPageable)),
        tap( data => this.store(data))
    ).subscribe({
      complete: () => {
        this.clearActivationData();
        this.activationModal.hide();
      },
      error: err => this.activationError = err
    });
  }

  hasError() {
    return !!this.activationError
  }

  getErrorMessage() {
    return this.activationError.userMessage || this.activationError.developerMessage || this.activationError;
  }
}
